import React from 'react'

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: 'primary' | 'secondary' | 'outline' | 'ghost'
    size?: 'sm' | 'md' | 'lg'
    children: React.ReactNode
    href?: string
}

const variantStyles = {
    primary: 'bg-dpxp-accent hover:bg-emerald-600 text-white shadow-lg hover:shadow-xl',
    secondary: 'bg-dpxp-secondary hover:bg-cyan-600 text-white shadow-md hover:shadow-lg',
    outline: 'border-2 border-dpxp-primary text-dpxp-primary hover:bg-dpxp-primary hover:text-white',
    ghost: 'text-dpxp-primary hover:bg-dpxp-primary/10',
}

const sizeStyles = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg',
}

export function Button({
    variant = 'primary',
    size = 'md',
    children,
    href,
    className = '',
    ...props
}: ButtonProps) {
    const baseStyles = 'inline-flex items-center justify-center font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 active:scale-100 focus:outline-none focus:ring-2 focus:ring-dpxp-primary focus:ring-offset-2'

    const combinedClassName = `${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${className}`

    if (href) {
        // mailto: e tel: abrem apps nativos, não precisam de target="_blank"
        const isExternalUrl = href.startsWith('http') || href.startsWith('https') || href.includes('wa.me')
        const isNativeLink = href.startsWith('mailto') || href.startsWith('tel')

        const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
            // Execute custom onClick if provided
            if (props.onClick) {
                (props.onClick as unknown as (e: React.MouseEvent<HTMLAnchorElement>) => void)(e)
            }
            // Força blur após click em mailto/tel para evitar estado travado
            if (isNativeLink) {
                (e.currentTarget as HTMLElement).blur()
            }
        }

        return (
            <a
                href={href}
                className={combinedClassName}
                target={isExternalUrl ? '_blank' : undefined}
                rel={isExternalUrl ? 'noopener noreferrer' : undefined}
                onClick={handleClick}
            >
                {children}
            </a>
        )
    }

    return (
        <button
            className={combinedClassName}
            {...props}
        >
            {children}
        </button>
    )
}
